/* eslint-disable @typescript-eslint/no-misused-promises */
import { Auth } from '@aws-amplify/auth';
import { MainFooter } from '@/components/Footer';
import { Header } from '@/components/Header';
import { MbeSettings } from '@/components/icons';
import { ROUTE_PATHS } from '@/constants';
import { useStickyHeader } from '@/hooks';
import { Box, Button, Container, Flex, Icon, Link } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, MenuMobile } from '../components';

interface IMyAccountLayout {
  children: React.ReactNode;
}

const commonActiveStyles = {
  content: '""',
  w: '100%',
  h: '8px',
  position: 'absolute',
  bg: '#e2001a',
};

export const MyAccountLayout: React.FC<IMyAccountLayout> = ({ children }): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useStickyHeader();

  const goToPage = (replace = false, path: string): void => {
    navigate(path, { replace });
  };

  const logout = async (): Promise<void> => {
    await Auth.signOut().catch(() => {
      throw new Error('Logout error');
    });

    goToPage(true, ROUTE_PATHS.landing);
  };

  return (
    <>
      <Header>
        <Flex
          alignItems="center"
          gap={{ md: 6, lg: 8 }}
          grow={{ base: 0, md: 1 }}
          justifyContent={{ base: 'unset', md: 'center', xl: 'unset' }}
        >
          <Menu
            activeStyle={{
              _before: {
                ...commonActiveStyles,
                bottom: '-50px',
              },
            }}
            listItemProps={{
              position: 'relative',
            }}
            listProps={{
              display: { base: 'none', xl: 'flex' },
              gap: { base: 4, md: 6, lg: 12 },
              id: 'main-nav',
            }}
          />
          <Button
            as="a"
            display={{ base: 'none', md: 'block' }}
            href={`/${ROUTE_PATHS.quote}`}
            variant="mbeSecondaryDark"
          >
            {t('ACCOUNT.MENU.CTA')}
          </Button>
          <NavLink to={ROUTE_PATHS.userSettings}>
            {({ isActive }): JSX.Element => (
              <Link
                as="span"
                display="block"
                {...(isActive && {
                  _before: {
                    ...commonActiveStyles,
                    w: { base: 8, md: 10 },
                    bottom: 0,
                  },
                })}
              >
                <Icon
                  as={MbeSettings}
                  color="white"
                  h={{ base: 8, md: 10 }}
                  w={{ base: 8, md: 10 }}
                />
              </Link>
            )}
          </NavLink>
          <Button
            _hover={{ textDecoration: 'none' }}
            display={{ base: 'none', md: 'block' }}
            px={0}
            variant="link"
            onClick={logout}
          >
            {t('ACCOUNT.MENU.LOGOUT')}
          </Button>
        </Flex>
        <MenuMobile />
      </Header>
      <main>{children}</main>
      <Box as="footer" bg="white" py={12}>
        <Container maxW="8xl">
          <MainFooter />
        </Container>
      </Box>
    </>
  );
};

export const PlainLayout: React.FC<IMyAccountLayout> = ({ children }): JSX.Element => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Box as="footer" bg="white" py={12}>
        <Container maxW={{ base: 'full', md: '8xl' }}>
          <MainFooter />
        </Container>
      </Box>
    </>
  );
};
